import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Download, Info, Moon, Sun, Globe, Loader2, FileText, AlertCircle } from "lucide-react";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function WebpageToPdf() {
  const [url, setUrl] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [error, setError] = useState("");
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Webpage to PDF - Convert URL to PDF - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const isValidUrl = (string) => {
    try {
      const url = new URL(string);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  const convertToPdf = async () => {
    setError("");
    
    if (!url.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    // Add https:// if not present
    let fullUrl = url.trim();
    if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
      fullUrl = 'https://' + fullUrl;
    }

    if (!isValidUrl(fullUrl)) {
      toast.error("Please enter a valid URL");
      return;
    }

    setIsProcessing(true);
    setProgress(10);
    setPdfBlob(null);

    try {
      setProgress(30);
      
      const response = await fetch(`${API_URL}/api/webpage-to-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: fullUrl }),
      });

      setProgress(70);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to convert webpage');
      }

      const blob = await response.blob();
      setProgress(90);

      // Extract filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'webpage.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      } else {
        // Generate filename from URL
        try {
          const urlObj = new URL(fullUrl);
          filename = `${urlObj.hostname.replace(/\./g, '_')}.pdf`;
        } catch (e) {
          filename = 'webpage.pdf';
        }
      }

      setPdfBlob(blob);
      setPdfFilename(filename);
      setProgress(100);
      toast.success("Webpage converted to PDF!");
    } catch (error) {
      console.error("Conversion error:", error);
      setError(error.message);
      toast.error("Failed to convert webpage: " + error.message);
    }

    setIsProcessing(false);
  };

  const downloadPdf = () => {
    if (!pdfBlob) return;
    const blobUrl = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = pdfFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  };

  const reset = () => {
    setUrl("");
    setPdfBlob(null);
    setPdfFilename("");
    setError("");
    setProgress(0);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-emerald-50 to-teal-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> Server-side
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full mb-4">
            <Globe className="w-5 h-5" />
            <span className="font-medium">Webpage to PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Convert Webpage to PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Enter any webpage URL to convert it to a downloadable PDF document.
          </p>
        </div>

        <Card className="max-w-2xl mx-auto dark:bg-slate-800">
          <CardContent className="p-8">
            <div className="space-y-6">
              <div>
                <Label className="text-base font-medium mb-2 block dark:text-white">
                  Webpage URL
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && convertToPdf()}
                    data-testid="url-input"
                  />
                  <Button
                    onClick={convertToPdf}
                    disabled={isProcessing}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600"
                    data-testid="convert-btn"
                  >
                    {isProcessing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Convert"
                    )}
                  </Button>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  Enter the full URL including https://
                </p>
              </div>

              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      Converting webpage...
                    </span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        Conversion Failed
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {pdfBlob && (
                <div className="p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                    PDF Ready!
                  </h3>
                  <p className="text-sm text-green-600 dark:text-green-300 mb-4">
                    {pdfFilename} • {(pdfBlob.size / 1024).toFixed(1)} KB
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button 
                      onClick={downloadPdf}
                      className="bg-gradient-to-r from-green-500 to-green-600"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                    <Button variant="outline" onClick={reset}>
                      Convert Another
                    </Button>
                  </div>
                </div>
              )}

              {/* Tips */}
              <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-2">Tips</h4>
                <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                  <li>• Use full URLs (e.g., https://example.com/page)</li>
                  <li>• Public pages work best (no login required)</li>
                  <li>• Some websites may block automated access</li>
                  <li>• Large pages may take longer to convert</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. Powered by server-side conversion.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default WebpageToPdf;
